// Enhanced AIStudioChat.tsx with Deploy Buttons
// Complete implementation with deploy functionality integrated

import React, {useState, useRef, useEffect, useCallback} from 'react';
import {Principal} from '@dfinity/principal';
import ClaudeChatService from "../claude/ClaudeChatService";
import useIDEStore from '../zustand/useIDEStore';
import FrontendMappingDialog from '../FrontendMappingDialog';
import EnhancedFileSelector from "./EnhancedFileSelector";

interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: number;
    fileContext?: string[];
    hasFileChanges?: boolean;
    extractedFiles?: { [key: string]: string };
}

interface ProjectFile {
    name: string;
    content: string;
    fullId?: string;
    path?: string;
    fileName?: string;
    id?: string;
}

interface AIStudioChatProps {
    activeFile: string;
    fileContent: string;
    files: ProjectFile[];
    project: any;
    onUpdateContent: (content: string) => void;
    onError: (error: string) => void;
    onSuccess: (message: string) => void;
    systemCanisters: any;
    userPrincipal: Principal;
    onFileSystemChange?: () => void;
    userCanisters: any[]; // 🆕 ADD this line
}

interface AIRule {
    id: string;
    name: string;
    description: string;
    content: string;
    type: 'custom' | 'url' | 'mcp' | 'template' | 'github';
    enabled: boolean;
    category: 'security' | 'style' | 'performance' | 'architecture' | 'general';
    url?: string;
    mcpServer?: string;
    lastUpdated?: number;
    status?: 'active' | 'loading' | 'error';
    errorMessage?: string;
    githubUrl?: string;
    githubPath?: string;
    githubBranch?: string;
    extractionType?: 'readme' | 'contributing' | 'docs' | 'examples' | 'all';
}

interface GitHubSource {
    id: string;
    name: string;
    githubUrl: string;
    githubPath?: string;
    githubBranch?: string;
    extractionType: 'readme' | 'contributing' | 'docs' | 'examples' | 'all';
    category: 'security' | 'style' | 'performance' | 'architecture' | 'general';
    description: string;
    status: 'active' | 'loading' | 'error';
    lastFetched?: number;
    contentPreview?: string;
    errorMessage?: string;
}

interface DocumentationSource {
    id: string;
    name: string;
    url: string;
    status: 'active' | 'loading' | 'error';
    contentPreview?: string;
}

interface MCPServer {
    id: string;
    name: string;
    url: string;
    status: 'connected' | 'connecting' | 'error' | 'disconnected';
    availableTools?: string[];
}

interface ActorCanisterMapping {
    actorFile: string;
    canisterId: string;
}

const AIStudioChat: React.FC<AIStudioChatProps> = ({
                                                       activeFile,
                                                       fileContent,
                                                       files,
                                                       project,
                                                       onUpdateContent,
                                                       onError,
                                                       onSuccess,
                                                       systemCanisters,
                                                       userPrincipal,
                                                       onFileSystemChange,
                                                       userCanisters
                                                   }) => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<string[]>(
        files.filter(file => file.name && file.name.trim().length > 0).map(file => file.name)
    );
    const [showFileSelector, setShowFileSelector] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [streamingContent, setStreamingContent] = useState('');
    const [isApplyingChanges, setIsApplyingChanges] = useState(false);
    const [applyProgress, setApplyProgress] = useState({progress: 0, message: ''});

    // Deploy-related state
    const [showClassInitArgsDialog, setShowClassInitArgsDialog] = useState(false);
    const [actorClassInfo, setActorClassInfo] = useState({className: '', initParams: ''});
    const [pendingDeploymentId, setPendingDeploymentId] = useState(null);
    const [pendingDeploymentMode, setPendingDeploymentMode] = useState(null);
    const [pendingProjectType, setPendingProjectType] = useState('icpstudio');
    const [initArgsValue, setInitArgsValue] = useState('');

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const chatService = useRef(new ClaudeChatService());

    // Get AI context from IDE store and localStorage
    const aiRules = useIDEStore(state => state.aiRules || []);
    const [docSources, setDocSources] = useState<DocumentationSource[]>([]);
    const [mcpServers, setMcpServers] = useState<MCPServer[]>([]);
    const [githubSources, setGithubSources] = useState<GitHubSource[]>([]);

    const [noCodeMode, setNoCodeMode] = useState(false);
    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [autoScroll, setAutoScroll] = useState(true);

    // Get deploy-related state from IDE store
    const compilationStatus = useIDEStore(state => state.compilationStatus);
    const compilerReady = useIDEStore(state => state.compilerReady);
    const deploymentError = useIDEStore(state => state.deploymentError);
    const deploy = useIDEStore(state => state.deploy);

    const [isDeployingAll, setIsDeployingAll] = useState<boolean>(false);
    const [deployStep, setDeployStep] = useState<string>('');

    const [availableActors, setAvailableActors] = useState<string[]>([]);
    const [selectedActor, setSelectedActor] = useState<string>('');
    const [selectedCanister, setSelectedCanister] = useState<string>('');
    const [showActorSelection, setShowActorSelection] = useState(false);
    const [deploymentContext, setDeploymentContext] = useState<'deployAll' | 'backendOnly' | null>(null);
    const [actorCanisterMappings, setActorCanisterMappings] = useState<ActorCanisterMapping[]>([]);
    const [showDeployAllDialog, setShowDeployAllDialog] = useState(false);
    const [showFrontendMappingDialog, setShowFrontendMappingDialog] = useState(false);
    const [pendingFrontendDeploy, setPendingFrontendDeploy] = useState(false);
    const [pendingCanisterId, setPendingCanisterId] = useState<string>('');



    useEffect(() => {
        console.log('🔍 AIStudioChat CANISTER DEBUG:');
        console.log('  - userCanisters:', userCanisters);
        console.log('  - userCanisters length:', userCanisters?.length || 0);
        console.log('  - project:', project);
        console.log('  - project.canisters:', project?.canisters);
        console.log('  - project.canisters length:', project?.canisters?.length || 0);

        if (userCanisters && userCanisters.length > 0) {
            console.log('  - All user canisters:');
            userCanisters.forEach((canister, index) => {
                console.log(`    ${index + 1}. ${canister.name || 'Unnamed'} (${canister.canisterType}): ${canister.principal.toText()}`);
            });
        }

        if (project?.canisters && project.canisters.length > 0) {
            console.log('  - Project canisters:');
            project.canisters.forEach((canister, index) => {
                const canisterText = typeof canister.toText === 'function' ? canister.toText() : String(canister);
                console.log(`    ${index + 1}. ${canisterText}`);
            });
        }

        // Test the filtering logic
        const projectBackendCanisters = userCanisters?.filter(c => {
            console.log(`  - Checking canister ${c.name || c.principal.toText()}:`);
            console.log(`    - Type: ${c.canisterType}`);
            console.log(`    - Is backend: ${c.canisterType === 'backend'}`);

            if (project?.canisters) {
                const isInProject = project.canisters.some((projectCanister) => {
                    const projectCanisterText = typeof projectCanister.toText === 'function'
                        ? projectCanister.toText()
                        : String(projectCanister);
                    const userCanisterText = c.principal.toText();
                    console.log(`    - Comparing: ${projectCanisterText} === ${userCanisterText} = ${projectCanisterText === userCanisterText}`);
                    return projectCanisterText === userCanisterText;
                });
                console.log(`    - Is in project: ${isInProject}`);
                return c.canisterType === 'backend' && isInProject;
            }
            return false;
        }) || [];

        console.log('  - Filtered backend canisters:', projectBackendCanisters);
        console.log('  - Filtered backend canisters count:', projectBackendCanisters.length);

    }, [userCanisters, project]);



    const debugCanisterVisibility = () => {
        console.log('🔍 MANUAL CANISTER DEBUG:');
        console.log('userCanisters:', userCanisters);
        console.log('project:', project);
        console.log('project.canisters:', project?.canisters);

        // Test different filtering approaches
        console.log('Testing different filtering approaches:');

        // Approach 1: Current logic
        const approach1 = userCanisters?.filter(c =>
            c.canisterType === 'backend' &&
            project.canisters.some((projectCanister) =>
                projectCanister.toText() === c.principal.toText()
            )
        ) || [];
        console.log('Approach 1 (current):', approach1);

        // Approach 2: String comparison
        const approach2 = userCanisters?.filter(c => {
            if (c.canisterType !== 'backend') return false;
            return project.canisters.some((projectCanister) => {
                const projectId = typeof projectCanister.toText === 'function'
                    ? projectCanister.toText()
                    : String(projectCanister);
                const canisterId = c.principal.toText();
                return projectId === canisterId;
            });
        }) || [];
        console.log('Approach 2 (string comparison):', approach2);

        // Approach 3: Just backend canisters
        const approach3 = userCanisters?.filter(c => c.canisterType === 'backend') || [];
        console.log('Approach 3 (all backend):', approach3);

        return {
            userCanisters: userCanisters || [],
            projectCanisters: project?.canisters || [],
            approach1,
            approach2,
            approach3
        };
    };



    // Load documentation sources and MCP servers from localStorage
    useEffect(() => {
        try {
            const savedDocs = localStorage.getItem('ide-doc-sources');
            const savedMcp = localStorage.getItem('ide-mcp-servers');
            const savedGitHub = localStorage.getItem('ide-github-sources');

            if (savedDocs) {
                setDocSources(JSON.parse(savedDocs));
            }
            if (savedMcp) {
                setMcpServers(JSON.parse(savedMcp));
            }
            if (savedGitHub) {
                setGithubSources(JSON.parse(savedGitHub));
            }
        } catch (error) {
            console.warn('Failed to load persisted context data:', error);
        }
    }, []);

    const scrollToBottom = () => {
        if (autoScroll) {
            messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
        }
    };

    useEffect(() => {
        if (messages.length > 0 || isStreaming) {
            scrollToBottom();
        }
    }, [messages, streamingContent]);

    const adjustTextareaHeight = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
        }
    }, []);

    useEffect(() => {
        adjustTextareaHeight();
    }, [inputValue, adjustTextareaHeight]);


    // Deploy functionality - adapted from your IDE component
    const isDeployableFile = (activeFile, fileContent) => {
        if (!activeFile) return false;

        // Check if the file is App.tsx - always deployable
        if (activeFile.endsWith('/App.tsx')) {
            return true;
        }

        // For Motoko files, use stricter patterns and specific file checks
        if (activeFile.endsWith('.mo')) {
            // First, check if it's main.mo or Main.mo (regardless of content)
            if (activeFile.endsWith('/main.mo') || activeFile.endsWith('/Main.mo')) {
                return true;
            }

            // For other .mo files, check for actual actor definitions more strictly
            // Remove comments first to avoid false positives
            const cleanedContent = fileContent
                .replace(/\/\/.*$/gm, '') // Remove line comments
                .replace(/\/\*[\s\S]*?\*\//g, ''); // Remove block comments

            // More precise regex patterns for actual actor declarations
            const actorPatterns = [
                /^actor\s+\w+\s*(\([^)]*\))?\s*\{/m,      // Named actor at start of line
                /^actor\s+class\s+\w+\s*(\([^)]*\))?\s*\{/m, // Actor class at start of line
                /^actor\s*\{/m,                               // Anonymous actor at start of line
                /export\s+actor\s+\w+\s*\{/m,                // Exported named actor
                /export\s+actor\s*\{/m                       // Exported anonymous actor
            ];

            return actorPatterns.some(pattern => pattern.test(cleanedContent));
        }

        return false;
    };

    const stopResponse = () => {
        console.log('🛑 STOP BUTTON CLICKED');
        if (abortController) {
            console.log('🛑 Aborting controller...');
            abortController.abort();
            setAbortController(null);
        }

        console.log('🛑 Setting streaming states to false...');
        setIsStreaming(false);
        setIsLoading(false);
        setStreamingContent('');
        console.log('🛑 Stop response complete');
    };

    // Check if all required mappings exist in localStorage
    const checkFrontendMappings = (): boolean => {
        if (availableActors.length === 0) return true; // No actors = no mappings needed

        const projectName = project?.name || 'unknown-project';

        for (const actorFile of availableActors) {
            const canisterName = getCanisterNameFromActor(actorFile);
            const storageKey = `${projectName}:${canisterName}`;
            const mapping = localStorage.getItem(storageKey);

            if (!mapping || mapping.trim() === '') {
                console.log(`❌ Missing mapping for ${storageKey}`);
                return false;
            }
        }

        console.log(`✅ All frontend mappings exist`);
        return true;
    };

    const executeFrontendDeploy = () => {
        console.log('🔵 Executing frontend deployment');

        // 🔥 CRITICAL: Switch to frontend type before deploying
        const store = useIDEStore.getState();
        store.setActiveType('frontend');

        deploy(null, null, 'frontend', 'icpstudio');
    };

    const handleFrontendDeploy = () => {
        console.log('🔵 Frontend deploy clicked');

        // If only one actor, auto-map and deploy directly
        if (availableActors.length <= 1) {
            console.log('🔵 Single/no actors - auto-deploying frontend directly');

            // Auto-store mapping if we have exactly one actor
            if (availableActors.length === 1) {
                // 🔧 FIXED: Filter canisters by project (matching deployment handler logic)
                const projectBackendCanisters = userCanisters?.filter(c =>
                    c.canisterType === 'backend' &&
                    project.canisters.some((projectCanister) =>
                        projectCanister.toText() === c.principal.toText()
                    )
                ) || [];

                if (projectBackendCanisters.length === 1) {
                    const autoActor = availableActors[0];
                    const autoCanister = projectBackendCanisters[0].principal.toText();

                    // Store the auto-mapping
                    const projectName = project?.name || 'unknown-project';
                    const canisterName = getCanisterNameFromActor(autoActor);
                    const storageKey = `${projectName}:${canisterName}`;

                    try {
                        localStorage.setItem(storageKey, autoCanister);
                        console.log(`💾 Frontend auto-stored mapping: ${storageKey} = ${autoCanister}`);
                    } catch (error) {
                        console.error('❌ Frontend auto-mapping storage failed:', error);
                    }
                }
            }

            // Deploy frontend directly
            executeFrontendDeploy();
            return;
        }

        // Multiple actors - always show dialog for user review/changes
        console.log('🔵 Multiple actors - showing mapping dialog for user review');
        setShowFrontendMappingDialog(true);
    };

    const handleFrontendMappingComplete = (mappings: Record<string, string>) => {
        console.log('✅ Frontend mappings completed:', mappings);
        setShowFrontendMappingDialog(false);

        // Now deploy frontend
        executeFrontendDeploy();
    };

    const handleDeployWithClassCheck = (deploymentId = null, installMode = 'reinstall', projectType = 'icpstudio') => {
        console.log("🔥 AIStudioChat handleDeployWithClassCheck called");

        // 🔧 FIXED: Filter canisters by project AND backend type (matching deployment handler logic)
        const projectBackendCanisters = userCanisters?.filter(c =>
            c.canisterType === 'backend' &&
            project.canisters.some((projectCanister) =>
                projectCanister.toText() === c.principal.toText()
            )
        ) || [];

        console.log(`🎯 AIStudioChat Deploy clicked - Found ${availableActors.length} actors and ${projectBackendCanisters.length} backend canisters for project ${project?.name}`);
        console.log(`📋 Available actors:`, availableActors);
        console.log(`📋 Project backend canisters:`, projectBackendCanisters.map(c => c.name || c.principal.toText()));

        // 🔧 EXPLICIT: Handle single actor scenarios first
        if (availableActors.length === 1) {
            const singleActor = availableActors[0];
            console.log(`✅ Single actor found: ${singleActor}`);

            if (projectBackendCanisters.length === 0) {
                console.log(`❌ No backend canisters found for project ${project?.name}`);
                onError(`No backend canisters found for project ${project?.name}`);
                return;
            }

            if (projectBackendCanisters.length === 1) {
                const singleCanister = projectBackendCanisters[0].principal.toText();
                console.log(`✅ Single project canister found: ${singleCanister}`);
                console.log(`🚀 AUTO-DEPLOYING: ${singleActor} → ${singleCanister}`);

                // Store the mapping in localStorage
                const projectName = project?.name || 'unknown-project';
                const canisterName = getCanisterNameFromActor(singleActor);
                const storageKey = `${projectName}:${canisterName}`;

                try {
                    localStorage.setItem(storageKey, singleCanister);
                    console.log(`💾 AIStudioChat STORED mapping: ${storageKey} = ${singleCanister}`);

                    // Verify storage
                    const verification = localStorage.getItem(storageKey);
                    if (verification !== singleCanister) {
                        console.error('❌ AIStudioChat storage verification failed!');
                    } else {
                        console.log('✅ AIStudioChat storage verification successful');
                    }
                } catch (error) {
                    console.error('❌ AIStudioChat localStorage.setItem failed:', error);
                }

                // Deploy immediately without showing any dialog
                executeBackendDeploy(deploymentId, installMode, projectType, singleActor, singleCanister);
                return; // IMPORTANT: Exit here to prevent dialog
            }

            if (projectBackendCanisters.length > 1) {
                console.log(`🔄 Single actor but ${projectBackendCanisters.length} project canisters - showing canister selection`);
                setSelectedActor(singleActor); // Pre-select the single actor
                setDeploymentContext('backendOnly');
                setShowActorSelection(true);
                return; // IMPORTANT: Exit here
            }
        }

        // 🔧 Handle multiple actors scenario
        if (availableActors.length > 1) {
            console.log(`🔄 Multiple actors (${availableActors.length}) found - showing actor selection dialog`);
            setDeploymentContext('backendOnly');
            setShowActorSelection(true);
            return; // IMPORTANT: Exit here
        }

        // 🔧 Handle no actors scenario
        if (availableActors.length === 0) {
            console.log(`❌ No actors found`);
            onError('No backend actors found to deploy');
            return;
        }

        // This should never be reached, but just in case
        console.error('❌ Unexpected scenario in handleDeployWithClassCheck');
        onError('Unexpected deployment scenario');
    };


    const handleClassInitArgsSubmit = (initArgs: string) => {
        console.log("Class init args:", initArgs);
        console.log("Pending canister ID:", pendingCanisterId);

        setShowClassInitArgsDialog(false);
        setInitArgsValue('');

        // FIXED: Pass the stored canister ID as 5th parameter
        deploy(
            pendingDeploymentId,
            initArgs,
            pendingDeploymentMode,
            pendingProjectType,
            pendingCanisterId
        );

        // Clear all pending state
        setPendingDeploymentId(null);
        setPendingDeploymentMode(null);
        setPendingProjectType('icpstudio');
        setPendingCanisterId(''); // FIXED: Clear the canister ID
    };

    const getCompleteFileContent = useCallback(async (fileName: string): Promise<string> => {
        console.log(`📖 CHAT: Getting complete content for file: ${fileName}`);

        if (fileName === activeFile) {
            return fileContent;
        }

        const fileObj = files.find(f => f.name === fileName);
        if (fileObj && fileObj.content && fileObj.content.trim().length > 0) {
            return fileObj.content;
        }

        if (fileObj && systemCanisters.userActor && userPrincipal && project?.id) {
            try {
                const result = await systemCanisters.userActor.getCodeArtifactContent(
                    userPrincipal, project.id, fileName, []
                );

                if ('ok' in result) {
                    const freshContent = 'Text' in result.ok ? result.ok.Text : '';
                    return freshContent;
                }
            } catch (error) {
                console.error(`❌ CHAT: Error loading fresh content for ${fileName}:`, error);
            }
        }

        return fileObj?.content || `// Content not available for ${fileName}`;
    }, [activeFile, fileContent, files, systemCanisters.userActor, userPrincipal, project?.id]);

    const buildComprehensiveContext = useCallback(async () => {
        // 🔧 FIXED: Only use selectedFiles - no automatic inclusion of activeFile
        // Active file can still be included if user explicitly selects it in File Context
        const filesToInclude = selectedFiles.length > 0
            ? selectedFiles  // Only files explicitly selected from File Context
            : [];            // No files if none selected

        console.log(`🔍 CHAT: Building comprehensive context for ${filesToInclude.length} files`);
        console.log(`📋 ACTIVE FILE: ${activeFile} (${filesToInclude.includes(activeFile) ? 'INCLUDED via selection' : 'NOT INCLUDED'})`);
        console.log(`📁 SELECTED FILES:`, filesToInclude);

        const selectedFileContents: { [fileName: string]: string } = {};

        // Process all selected files (including active file if it was explicitly selected)
        for (const fileName of filesToInclude) {
            if (fileName) { // No need to exclude activeFile anymore
                try {
                    const content = await getCompleteFileContent(fileName);
                    selectedFileContents[fileName] = content;
                } catch (error) {
                    console.error(`❌ CHAT: Failed to load content for ${fileName}:`, error);
                    selectedFileContents[fileName] = `// Error loading content for ${fileName}`;
                }
            }
        }

        // Get enhanced AI context including all sources
        const enhancedAIContext = getEnhancedAIContext();

        const context = {
            activeFile,      // Still pass activeFile for reference, but don't include its content
            fileContent,     // Current file content is available separately
            selectedFiles: filesToInclude,
            selectedFileContents,
            fileContents: selectedFileContents,
            projectStructure: files.map(f => ({
                name: f.name,
                type: f.name.split('.').pop() || 'unknown'
            })),
            projectInfo: {
                id: project?.id || '',
                name: project?.name || '',
                type: project?.projectType || 'unknown'
            },
            aiRulesContext: enhancedAIContext
        };

        console.log(`📁 CHAT: Context built with ${Object.keys(selectedFileContents).length} files from File Context selector`);
        return context;
    }, [selectedFiles, files, project, getCompleteFileContent, activeFile, fileContent]);


    const getEnhancedAIContext = (): string => {
        let context = '';

        // 1. Active AI Rules
        const enabledRules = aiRules.filter(rule => rule.enabled && rule.status === 'active');
        if (enabledRules.length > 0) {
            const rulesByCategory = enabledRules.reduce((acc, rule) => {
                if (!acc[rule.category]) acc[rule.category] = [];
                acc[rule.category].push(rule);
                return acc;
            }, {} as Record<string, AIRule[]>);

            context += '\n\n=== AI ASSISTANT RULES ===\n';
            context += 'Please follow these specific guidelines when providing assistance:\n\n';

            Object.entries(rulesByCategory).forEach(([category, rules]) => {
                context += `## ${category.toUpperCase()} GUIDELINES:\n`;
                rules.forEach(rule => {
                    context += `- ${rule.name}: ${rule.content}\n`;
                });
                context += '\n';
            });

            context += '=== END RULES ===\n\n';
        }

        // 2. Documentation Sources
        const activeDocs = docSources.filter(doc => doc.status === 'active' && doc.contentPreview);
        if (activeDocs.length > 0) {
            context += '\n\n=== DOCUMENTATION CONTEXT ===\n';
            context += 'Reference these documentation sources when providing assistance:\n\n';

            activeDocs.forEach(doc => {
                context += `## ${doc.name.toUpperCase()}\n`;
                context += `Source: ${doc.url}\n`;
                context += `Content: ${doc.contentPreview}\n\n`;
            });

            context += '=== END DOCUMENTATION ===\n\n';
        }

        // 3. GitHub Sources
        const activeGitHub = githubSources.filter(github => github.status === 'active' && github.contentPreview);
        if (activeGitHub.length > 0) {
            context += '\n\n=== GITHUB REPOSITORY CONTEXT ===\n';
            context += 'Reference these GitHub repository guidelines when providing assistance:\n\n';

            activeGitHub.forEach(github => {
                context += `## ${github.name.toUpperCase()}\n`;
                context += `Repository: ${github.githubUrl}\n`;
                context += `Extraction Type: ${github.extractionType}\n`;
                context += `Category: ${github.category}\n`;
                context += `Guidelines: ${github.contentPreview}\n\n`;
            });

            context += '=== END GITHUB CONTEXT ===\n\n';
        }

        // 4. MCP Server Capabilities
        const connectedMcp = mcpServers.filter(server => server.status === 'connected');
        if (connectedMcp.length > 0) {
            context += '\n\n=== MCP CAPABILITIES ===\n';
            context += 'Available MCP servers and their tools:\n\n';

            connectedMcp.forEach(server => {
                context += `## ${server.name.toUpperCase()}\n`;
                context += `Server: ${server.url}\n`;
                if (server.availableTools && server.availableTools.length > 0) {
                    context += `Available Tools: ${server.availableTools.join(', ')}\n`;
                }
                context += 'Note: You can leverage these MCP capabilities for enhanced functionality.\n\n';
            });

            context += '=== END MCP CAPABILITIES ===\n\n';
        }

        // 5. Context Summary
        if (enabledRules.length > 0 || activeDocs.length > 0 || activeGitHub.length > 0 || connectedMcp.length > 0) {
            context += '\n\n=== CONTEXT SUMMARY ===\n';
            context += `Active Elements:\n`;
            if (enabledRules.length > 0) context += `- ${enabledRules.length} AI rules enforced\n`;
            if (activeDocs.length > 0) context += `- ${activeDocs.length} web documentation sources available\n`;
            if (activeGitHub.length > 0) context += `- ${activeGitHub.length} GitHub repository sources available\n`;
            if (connectedMcp.length > 0) context += `- ${connectedMcp.length} MCP servers connected\n`;
            context += '\nPlease consider all these sources when providing assistance.\n';
            context += '=== END CONTEXT SUMMARY ===\n\n';
        }

        return context;
    };

    const getApiKeyFromPlatform = async (): Promise<string> => {
        if (!systemCanisters.platformActor) {
            throw new Error('Platform canister not initialized');
        }

        try {
            const result = await systemCanisters.platformActor.getClaudeApiKey();
            if ('ok' in result) {
                return result.ok;
            } else {
                throw new Error(result.err || 'Failed to retrieve Claude API key');
            }
        } catch (error) {
            console.error('Error fetching API key from platform:', error);
            throw new Error(`Claude API not configured: ${error instanceof Error ? error.message : String(error)}`);
        }
    };

    const getApiKeyFromLocalStorage = async (): Promise<string> => {
        console.log('🔍 getApiKeyFromLocalStorage() called in AIStudioChat');

        // Get API key from localStorage
        const localStorageKey = localStorage.getItem('claude_api_key');
        console.log('🔑 AIStudioChat localStorage key:', localStorageKey ? `${localStorageKey.substring(0, 15)}...` : 'null');
        console.log('🔑 Full localStorage contents:', Object.keys(localStorage));

        if (localStorageKey && localStorageKey.trim()) {
            console.log('✅ AIStudioChat returning localStorage key:', `${localStorageKey.substring(0, 15)}...`);
            return localStorageKey.trim();
        }

        // If no API key found, throw error
        console.log('❌ AIStudioChat: No API key found in localStorage');
        throw new Error('Claude API key not found. Please set your API key in the project settings.');

        /* PLATFORM CANISTER METHOD - COMMENTED OUT FOR FUTURE USE
        console.log('🔄 AIStudioChat: Would try platform canister here...');
        if (!systemCanisters.platformActor) {
            throw new Error('Platform canister not initialized');
        }

        try {
            const result = await systemCanisters.platformActor.getClaudeApiKey();
            if ('ok' in result) {
                console.log('✅ AIStudioChat: Got key from platform canister');
                return result.ok;
            } else {
                throw new Error(result.err || 'Failed to retrieve Claude API key');
            }
        } catch (error) {
            console.error('Error fetching API key from platform:', error);
            throw new Error(`Claude API not configured: ${error instanceof Error ? error.message : String(error)}`);
        }
        */
    };

    const applyFileChanges = async (extractedFiles: { [key: string]: string }) => {
        if (!systemCanisters.userActor || !userPrincipal || !project?.id) {
            throw new Error('Required dependencies not available for file operations');
        }

        setIsApplyingChanges(true);
        setApplyProgress({progress: 10, message: 'Analyzing file changes...'});

        try {
            console.log(`🔧 CHAT: Applying ${Object.keys(extractedFiles).length} file changes`);

            let uploadedCount = 0;
            let updatedCount = 0;
            const totalFiles = Object.keys(extractedFiles).length;

            for (const [fileName, fileContent] of Object.entries(extractedFiles)) {
                if (!fileContent || typeof fileContent !== 'string' || !fileContent.trim()) {
                    continue;
                }

                let cleanFileName = fileName;
                cleanFileName = cleanFileName.replace(/^.*?Complete file:\s*/i, '');
                cleanFileName = cleanFileName.replace(/^.*?\/\/\s*/, '');
                cleanFileName = cleanFileName.replace(/^.*?\/\*.*?\*\/\s*/s, '');
                cleanFileName = cleanFileName.trim();

                const extension = cleanFileName.substring(cleanFileName.lastIndexOf('.')).toLowerCase();

                if (!['.mo', '.tsx', '.ts', '.css', '.js', '.jsx', '.json'].includes(extension)) {
                    continue;
                }

                let filePath = '';
                let fileType = '';
                let languageType = '';
                let finalFileName = cleanFileName;

                // FIXED: Handle file paths correctly without creating nested project directories
                if (cleanFileName.includes('/')) {
                    const pathParts = cleanFileName.split('/');
                    finalFileName = pathParts.pop() || cleanFileName;

                    // IMPORTANT: Don't prepend project name - use the path as-is
                    filePath = pathParts.join('/');

                    // Only add project name if the path doesn't already start with the project structure
                    if (!filePath.startsWith('src/') && !filePath.startsWith(project.name)) {
                        filePath = `${project.name}/${filePath}`;
                    } else if (filePath.startsWith('src/')) {
                        // Path already starts with src/, so just prepend project name
                        filePath = `${project.name}/${filePath}`;
                    }
                } else {
                    // File without path - use default locations
                    if (['.tsx', '.ts', '.css', '.js', '.jsx'].includes(extension)) {
                        filePath = `${project.name}/src/frontend/src`;
                    } else if (extension === '.mo') {
                        filePath = `${project.name}/src/backend/src`;
                    } else if (['.json'].includes(extension)) {
                        // Config files go in frontend root
                        filePath = `${project.name}/src/frontend`;
                    } else {
                        filePath = project.name;
                    }
                }

                // Set file type and language type
                if (extension === '.css') {
                    fileType = 'text/css';
                    languageType = 'css';
                } else if (['.tsx', '.ts', '.js', '.jsx'].includes(extension)) {
                    fileType = 'text/typescript';
                    languageType = 'typescript';
                } else if (extension === '.mo') {
                    fileType = 'text/x-motoko';
                    languageType = 'motoko';
                } else if (extension === '.json') {
                    fileType = 'application/json';
                    languageType = 'json';
                }

                console.log(`📁 PROCESSING FILE:`, {
                    originalPath: fileName,
                    cleanFileName: cleanFileName,
                    finalFileName: finalFileName,
                    filePath: filePath,
                    fullPath: `${filePath}/${finalFileName}`
                });

                try {
                    const createResult = await systemCanisters.userActor.createCodeArtifact(
                        userPrincipal,
                        project.id,
                        finalFileName,
                        {Text: fileContent},
                        fileType,
                        languageType,
                        filePath,
                        []
                    );

                    if ('ok' in createResult) {
                        uploadedCount++;
                        console.log(`✅ CREATED: ${filePath}/${finalFileName}`);
                    } else {
                        const updateResult = await systemCanisters.userActor.updateCodeArtifact(
                            userPrincipal,
                            project.id,
                            finalFileName,
                            {Text: fileContent},
                            filePath,
                            []
                        );

                        if ('ok' in updateResult) {
                            updatedCount++;
                            console.log(`✅ UPDATED: ${filePath}/${finalFileName}`);
                        }
                    }
                } catch (error) {
                    console.error(`💥 CHAT: Error processing ${finalFileName}:`, error);
                }

                const processedCount = uploadedCount + updatedCount;
                const fileProgress = 30 + ((processedCount / totalFiles) * 60);
                setApplyProgress({
                    progress: Math.round(fileProgress),
                    message: `Processed ${processedCount}/${totalFiles} files...`
                });
            }

            setApplyProgress({progress: 95, message: 'Refreshing file system...'});

            if (onFileSystemChange) {
                onFileSystemChange();
            }

            const totalProcessed = uploadedCount + updatedCount;
            setApplyProgress({
                progress: 100,
                message: `✅ Applied changes to ${totalProcessed} files! (${uploadedCount} created, ${updatedCount} updated)`
            });

            onSuccess(`Successfully applied changes: ${uploadedCount} files created, ${updatedCount} files updated`);

            setTimeout(() => {
                setIsApplyingChanges(false);
                setApplyProgress({progress: 0, message: ''});
            }, 3000);

        } catch (error) {
            console.error('CHAT: Error applying file changes:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            setApplyProgress({progress: 0, message: `Error: ${errorMessage}`});
            onError(`Failed to apply changes: ${errorMessage}`);

            setTimeout(() => {
                setIsApplyingChanges(false);
                setApplyProgress({progress: 0, message: ''});
            }, 5000);
        }
    };

    const sendMessage = async (messageText?: string) => {
        let textToSend = messageText || inputValue.trim();
        if (!textToSend || isLoading) return;

        const controller = new AbortController(); // 🆕 ADD THIS
        setAbortController(controller); // 🆕 ADD THIS


        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: textToSend,
            timestamp: Date.now(),
            fileContext: selectedFiles.length > 0 ? selectedFiles : [activeFile]
        };

        if (noCodeMode && !textToSend.toLowerCase().includes('no code')) {
            textToSend += '\n\nPlease provide explanations and guidance without generating any code blocks or file modifications.';
        }

        const cleanMessages = messages.filter(msg => msg.content && msg.content.trim().length > 0);
        const newMessages = [...cleanMessages, userMessage];
        setMessages(newMessages);
        setInputValue('');
        setIsLoading(true);
        setIsStreaming(true);
        setStreamingContent('');

        try {
            const apiKey = await getApiKeyFromLocalStorage();
            const context = await buildComprehensiveContext();

            const enhancedAIContext = getEnhancedAIContext();
            const enhancedPrompt = `${textToSend}

${enhancedAIContext}

When providing code solutions, please provide COMPLETE file content when modifying existing files. Use this format for each file:

\`\`\`typescript
// Complete file: src/frontend/src/FileName.tsx
[COMPLETE FILE CONTENT WITH ALL IMPORTS, FUNCTIONS, AND MODIFICATIONS]
\`\`\`

For any code changes, include the entire file content, not just snippets.`;

            console.log(`💬 CHAT: Sending request with enhanced AI context`);
            console.log(`📁 CONTEXT: ${Object.keys(context.fileContents || {}).length} complete files`);
            console.log(`🎯 AI RULES: ${aiRules.filter(r => r.enabled).length} active`);
            console.log(`📚 DOCS: ${docSources.filter(d => d.status === 'active').length} sources`);
            console.log(`🔌 MCP: ${mcpServers.filter(m => m.status === 'connected').length} connected`);

            const finalMessages = [...cleanMessages, {...userMessage, content: enhancedPrompt}];

            const hasEmptyMessages = finalMessages.some(msg => !msg.content || msg.content.trim().length === 0);
            if (hasEmptyMessages) {
                throw new Error('Cannot send request: Empty messages detected in conversation');
            }

            await chatService.current.sendStreamingChatWithFileExtraction(
                apiKey,
                finalMessages,
                context,
                true,
                (chunk: string) => {
                    // Use the controller variable directly, not the state
                    if (controller.signal.aborted) {
                        console.log('🛑 Chunk rejected - aborted');
                        return;
                    }
                    setStreamingContent(prev => prev + chunk);
                },
                async (finalResponse: string, files: { [key: string]: string } = {}) => {
                    // Use the controller variable directly, not the state
                    if (controller.signal.aborted) {
                        console.log('🛑 Completion rejected - aborted');
                        return;
                    }

                    console.log('🎯 STREAMING COMPLETION CALLBACK TRIGGERED');
                    console.log(`✅ CHAT: Response complete (${finalResponse.length} chars, ${Object.keys(files).length} files)`);

                    if (finalResponse && finalResponse.trim().length > 0) {
                        const assistantMessage: ChatMessage = {
                            id: (Date.now() + 1).toString(),
                            role: 'assistant',
                            content: finalResponse.trim(),
                            timestamp: Date.now(),
                            hasFileChanges: Object.keys(files).length > 0,
                            extractedFiles: files
                        };

                        const finalMessagesWithResponse = [...newMessages, assistantMessage];
                        setMessages(finalMessagesWithResponse);
                    } else {
                        onError('Received empty response from AI assistant');
                    }

                    setStreamingContent('');
                    setIsStreaming(false);
                    setAbortController(null);
                }
            );

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            onError(`Chat error: ${errorMessage}`);

            const errorContent = `Sorry, I encountered an error: ${errorMessage}`;
            if (errorContent.trim().length > 0) {
                const errorMsg: ChatMessage = {
                    id: (Date.now() + 1).toString(),
                    role: 'assistant',
                    content: errorContent,
                    timestamp: Date.now()
                };
                setMessages(prev => [...prev.filter(msg => msg.content && msg.content.trim().length > 0), errorMsg]);
            }

            setStreamingContent('');
            setIsStreaming(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const clearConversation = () => {
        setMessages([]);
        setStreamingContent('');
        console.log('🧹 CHAT: Conversation cleared');
    };

    const detectCompleteFiles = (content: string): { [key: string]: string } => {
        const files: { [key: string]: string } = {};

        console.log('🔍 DETECTING COMPLETE FILES in content:', content.substring(0, 200) + '...');

        // Enhanced patterns that match the actual format from the AI response
        const patterns = [
            // TypeScript/JavaScript files with various comment formats
            {
                regex: /```(?:typescript|ts|javascript|js|tsx|jsx)\s*\n(?:\/\/\s*Complete file:\s*([^\n]+\.(?:tsx?|jsx?|js))[^\n]*\n)?([\s\S]*?)```/gi,
                type: 'typescript'
            },
            // CSS files
            {
                regex: /```css\s*\n(?:\/\*\s*Complete file:\s*([^\n*]+\.css)[^\n*]*\s*\*\/\s*\n)?([\s\S]*?)```/gi,
                type: 'css'
            },
            // Motoko files
            {
                regex: /```motoko\s*\n(?:\/\/\s*Complete file:\s*([^\n]+\.mo)[^\n]*\n)?([\s\S]*?)```/gi,
                type: 'motoko'
            },
            // Generic pattern for any language with "Complete file:" comment
            {
                regex: /```\w*\s*\n(?:(?:\/\/|\/\*)\s*Complete file:\s*([^\n\*]+)(?:\*\/)?\s*\n)([\s\S]*?)```/gi,
                type: 'generic'
            },
            // Pattern for files without explicit "Complete file:" but with file paths in comments
            {
                regex: /```(?:typescript|ts|tsx|jsx|javascript|js)\s*\n(?:\/\/\s*(src\/[^\n]+\.(?:tsx?|jsx?|js))[^\n]*\n)([\s\S]*?)```/gi,
                type: 'typescript-path'
            }
        ];

        patterns.forEach(({regex, type}, patternIndex) => {
            let match;
            while ((match = regex.exec(content)) !== null) {
                console.log(`📁 PATTERN ${patternIndex} (${type}) MATCH:`, {
                    fullMatch: match[0].substring(0, 100) + '...',
                    filename: match[1],
                    contentPreview: match[2]?.substring(0, 100) + '...'
                });

                let filename = match[1]?.trim();
                let fileContent = match[2]?.trim();

                // If no filename in capture group 1, try to extract from the content
                if (!filename || filename.length === 0) {
                    // Look for filename in the first few lines of content
                    const lines = fileContent?.split('\n').slice(0, 5) || [];
                    for (const line of lines) {
                        const fileMatch = line.match(/(?:\/\/|\/\*)\s*(?:Complete file:|File:|src\/[^\s*]+\.(?:tsx?|jsx?|js|css|mo))/i);
                        if (fileMatch) {
                            const extracted = line.match(/(?:src\/[^\s*]+|[^\s*]+\.(?:tsx?|jsx?|js|css|mo))/i);
                            if (extracted) {
                                filename = extracted[0].replace(/\*\/.*$/, '').trim();
                                break;
                            }
                        }
                    }
                }

                if (!filename && fileContent) {
                    // Try to infer filename from imports or content
                    const importMatch = fileContent.match(/import.*from\s+['"]\.\/([^'"]+)['"]/);
                    if (importMatch) {
                        filename = `src/frontend/src/${importMatch[1]}`;
                        if (!filename.includes('.')) {
                            filename += type === 'typescript' ? '.tsx' : '.js';
                        }
                    }
                }

                if (filename && fileContent && fileContent.length > 50) {
                    // Clean up filename
                    filename = filename.replace(/^.*\/\/\s*/, '');
                    filename = filename.replace(/^.*\/\*\s*/, '');
                    filename = filename.replace(/\s*\*\/.*$/, '');
                    filename = filename.replace(/^Complete file:\s*/i, '');
                    filename = filename.trim();

                    // Ensure proper file extension
                    if (!filename.includes('.')) {
                        if (type === 'css') filename += '.css';
                        else if (type === 'motoko') filename += '.mo';
                        else filename += '.tsx';
                    }

                    // Ensure proper path structure
                    if (!filename.includes('/')) {
                        const ext = filename.substring(filename.lastIndexOf('.')).toLowerCase();
                        if (ext === '.mo') {
                            filename = `src/backend/src/${filename}`;
                        } else if (['.css'].includes(ext)) {
                            filename = `src/frontend/src/${filename}`;
                        } else {
                            filename = `src/frontend/src/${filename}`;
                        }
                    }

                    // Clean up content - remove any leading comments about the file
                    fileContent = fileContent.replace(/^(?:\/\/|\/\*).*?(?:Complete file|File):.*?\n/i, '');
                    fileContent = fileContent.replace(/^\/\*[\s\S]*?\*\/\s*\n/, '');
                    fileContent = fileContent.trim();

                    console.log(`✅ EXTRACTED FILE:`, {
                        filename: filename,
                        contentLength: fileContent.length,
                        contentPreview: fileContent.substring(0, 150) + '...'
                    });

                    files[filename] = fileContent;
                }
            }
        });

        console.log(`🎯 FINAL DETECTED FILES:`, Object.keys(files));
        return files;
    };

    const extractCodeBlocks = (content: string): Array<{ language: string; code: string }> => {
        const codeBlockRegex = /```(\w*)\s*\n([\s\S]*?)```/g;
        const blocks: Array<{ language: string; code: string }> = [];
        let match;

        while ((match = codeBlockRegex.exec(content)) !== null) {
            const language = match[1] || 'text';
            const code = match[2].trim();

            if (code && code.length > 10) {
                blocks.push({
                    language: language,
                    code: code
                });
            }
        }

        return blocks;
    };

    const getContextSummary = () => {
        const enabledRules = aiRules.filter(r => r.enabled).length;
        const activeDocs = docSources.filter(d => d.status === 'active').length;
        const activeGitHub = githubSources.filter(g => g.status === 'active').length;
        const connectedMcp = mcpServers.filter(m => m.status === 'connected').length;
        const selectedFileCount = selectedFiles.length;

        return {
            enabledRules,
            activeDocs,
            activeGitHub,
            connectedMcp,
            selectedFileCount,
            hasEnhancedContext: enabledRules > 0 || activeDocs > 0 || activeGitHub > 0 || connectedMcp > 0
        };
    };

    const formatTimestamp = (timestamp: number) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const getFileTypeIcon = (fileName: string) => {
        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'mo':
                return '🟣';
            case 'tsx':
            case 'ts':
                return '🔵';
            case 'jsx':
            case 'js':
                return '🟡';
            case 'css':
                return '🎨';
            case 'json':
                return '📋';
            case 'md':
                return '📝';
            default:
                return '📄';
        }
    };

    const generateQuickSuggestions = () => {
        const allContent = Object.values(files).map(f => f.content || '').join('\n').toLowerCase();
        const suggestions = [];

        if (allContent.includes('todo') || allContent.includes('fixme')) {
            suggestions.push("Implement all TODO items and fix FIXME comments");
        }

        if (allContent.includes('error') || allContent.includes('bug')) {
            suggestions.push("Debug and fix any errors in the code");
        }

        if (selectedFiles.length > 1) {
            suggestions.push("Analyze all selected files and improve consistency");
            suggestions.push("Refactor code across multiple files");
        }

        const hasMotoko = files.some(f => f.name.endsWith('.mo'));
        const hasReact = files.some(f => f.name.endsWith('.tsx') || f.name.endsWith('.jsx'));

        if (hasMotoko && hasReact) {
            suggestions.push("Improve backend-frontend integration");
            suggestions.push("Add type safety between Motoko and TypeScript");
        }

        suggestions.push("Add comprehensive error handling");
        suggestions.push("Improve code documentation and comments");
        suggestions.push("Optimize performance and code quality");
        suggestions.push("Add proper TypeScript types and interfaces");

        return suggestions.slice(0, 6);
    };


    const renderNoCodeToggle = () => {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                backgroundColor: noCodeMode ? '#e8f5e9' : '#fff3cd',
                border: `1px solid ${noCodeMode ? '#4caf50' : '#ffc107'}`,
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500'
            }}>
            <span style={{color: noCodeMode ? '#2e7d32' : '#856404'}}>
                💬 No Code Mode
            </span>
                <button
                    onClick={() => setNoCodeMode(!noCodeMode)}
                    style={{
                        width: '40px',
                        height: '20px',
                        borderRadius: '10px',
                        border: 'none',
                        backgroundColor: noCodeMode ? '#4caf50' : '#ccc',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                    }}
                >
                    <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        position: 'absolute',
                        top: '2px',
                        left: noCodeMode ? '22px' : '2px',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                    }}/>
                </button>
                <span style={{
                    fontSize: '10px',
                    color: noCodeMode ? '#2e7d32' : '#856404',
                    fontWeight: '600'
                }}>
                {noCodeMode ? 'ON' : 'OFF'}
            </span>
            </div>
        );
    };

    // Render functions
    const renderFileSelector = () => {
        if (!showFileSelector) return null;

        return (
            <EnhancedFileSelector
                files={files}
                selectedFiles={selectedFiles}
                onSelectionChange={setSelectedFiles}
            />
        );
    };

    const renderApplyProgress = () => {
        if (!isApplyingChanges) return null;

        return (
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
            }}>
                <div style={{
                    backgroundColor: '#fdf6e3',
                    padding: '32px',
                    borderRadius: '16px',
                    border: '2px solid #8B4513',
                    minWidth: '400px',
                    textAlign: 'center',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                }}>
                    <h3 style={{
                        margin: '0 0 20px 0',
                        color: '#5C4033',
                        fontSize: '18px',
                        fontWeight: '600'
                    }}>
                        🔧 Applying AI Changes
                    </h3>

                    <div style={{
                        width: '100%',
                        height: '12px',
                        backgroundColor: 'rgba(139, 69, 19, 0.2)',
                        borderRadius: '6px',
                        marginBottom: '16px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${applyProgress.progress}%`,
                            height: '100%',
                            backgroundColor: '#4B5320',
                            borderRadius: '6px',
                            transition: 'width 0.3s ease'
                        }}/>
                    </div>

                    <p style={{
                        margin: 0,
                        color: '#8B4513',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}>
                        {applyProgress.message}
                    </p>

                    <div style={{
                        marginTop: '12px',
                        fontSize: '12px',
                        color: '#5C4033',
                        fontWeight: '600'
                    }}>
                        {applyProgress.progress}% Complete
                    </div>
                </div>
            </div>
        );
    };

    const renderMessage = (message: ChatMessage) => {
        if (message.role === 'user') {
            return (
                <div key={message.id} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    alignSelf: 'flex-end',
                    maxWidth: '75%',
                    marginBottom: '20px'
                }}>
                    <div style={{
                        fontSize: '12px',
                        color: '#8B4513',
                        fontWeight: '500',
                        marginLeft: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <span>You</span>
                        <span style={{fontSize: '10px', opacity: 0.7}}>
                        {formatTimestamp(message.timestamp)}
                    </span>
                    </div>
                    <div style={{
                        padding: '16px 20px',
                        borderRadius: '16px 16px 4px 16px',
                        backgroundColor: '#5C4033',
                        color: '#FFF5EE',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        position: 'relative'
                    }}>
                        {message.content}
                        {message.fileContext && message.fileContext.length > 0 && (
                            <div style={{
                                marginTop: '12px',
                                paddingTop: '12px',
                                borderTop: '1px solid rgba(255, 245, 238, 0.2)',
                                fontSize: '11px',
                                opacity: 0.8
                            }}>
                                📁 Context: {message.fileContext.length} file{message.fileContext.length > 1 ? 's' : ''}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        // FIXED: Use the enhanced detectCompleteFiles function
        const backendFiles = message.extractedFiles || {};
        const detectedFiles = detectCompleteFiles(message.content);
        const allFiles = {...detectedFiles, ...backendFiles};
        const codeBlocks = extractCodeBlocks(message.content);

        const hasFiles = Object.keys(allFiles).length > 0;
        const hasSingleCode = codeBlocks.length > 0 && !hasFiles;
        const showApplyButton = hasFiles || hasSingleCode;

        console.log(`🎯 MESSAGE ANALYSIS:`, {
            messageId: message.id,
            backendFiles: Object.keys(backendFiles),
            detectedFiles: Object.keys(detectedFiles),
            allFiles: Object.keys(allFiles),
            hasFiles,
            hasSingleCode,
            showApplyButton
        });

        return (
            <div key={message.id} style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                alignSelf: 'flex-start',
                maxWidth: '75%',
                marginBottom: '20px'
            }}>
                <div style={{
                    fontSize: '12px',
                    color: '#8B4513',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <span>🤖 AI Assistant</span>
                    <span style={{fontSize: '10px', opacity: 0.7}}>
                    {formatTimestamp(message.timestamp)}
                </span>
                </div>

                <div style={{
                    padding: '16px 20px',
                    borderRadius: '16px 16px 16px 4px',
                    backgroundColor: '#f3e5d8',
                    color: '#5C4033',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    border: '1px solid #e5d8c8'
                }}>
                    {message.content}
                </div>

                {showApplyButton && (
                    <div style={{
                        backgroundColor: 'rgba(75, 83, 32, 0.1)',
                        border: '1px solid #4B5320',
                        borderRadius: '12px',
                        padding: '16px',
                        marginTop: '8px'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '12px'
                        }}>
                            <div>
                                <div style={{
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#4B5320',
                                    marginBottom: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    🔧 {hasFiles ? `${Object.keys(allFiles).length} Files Ready` : 'Code Block Ready'}
                                    {hasFiles && (
                                        <span style={{
                                            fontSize: '10px',
                                            backgroundColor: 'rgba(75, 83, 32, 0.2)',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            fontWeight: '500'
                                        }}>
                                        COMPLETE FILES
                                    </span>
                                    )}
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    color: '#8B4513',
                                    lineHeight: '1.4'
                                }}>
                                    {hasFiles ?
                                        `Ready to apply: ${Object.keys(allFiles).join(', ')}` :
                                        'Apply code block to current file'
                                    }
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    if (hasFiles) {
                                        applyFileChanges(allFiles);
                                    } else if (codeBlocks.length > 0) {
                                        let cleanContent = codeBlocks[0].code;
                                        cleanContent = cleanContent.replace(/```[\w]*\n?/g, '');
                                        cleanContent = cleanContent.replace(/```\n?/g, '');
                                        cleanContent = cleanContent.replace(/^\/\/ [^\n]*\n/, '');
                                        cleanContent = cleanContent.replace(/^\/\* [^\n]* \*\/\n/, '');
                                        cleanContent = cleanContent.trim();

                                        onUpdateContent(cleanContent);
                                        onSuccess('Code applied to current file');
                                    }
                                }}
                                disabled={isApplyingChanges}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: isApplyingChanges ? '#9B5523' : '#4B5320',
                                    color: '#FFFFFF',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: isApplyingChanges ? 'not-allowed' : 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isApplyingChanges) {
                                        e.currentTarget.style.backgroundColor = '#3a4018';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isApplyingChanges) {
                                        e.currentTarget.style.backgroundColor = '#4B5320';
                                    }
                                }}
                            >
                                {isApplyingChanges ? (
                                    <>
                                        <div style={{
                                            width: '14px',
                                            height: '14px',
                                            border: '2px solid rgba(255,255,255,0.3)',
                                            borderTopColor: '#FFF',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite'
                                        }}/>
                                        Applying...
                                    </>
                                ) : (
                                    <>
                                        {hasFiles ? '🔧 Apply All Files' : '📝 Apply Code'}
                                    </>
                                )}
                            </button>
                        </div>

                        {hasFiles && (
                            <div style={{
                                fontSize: '11px',
                                color: '#8B4513',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '6px'
                            }}>
                                {Object.keys(allFiles).map(fileName => (
                                    <span key={fileName} style={{
                                        backgroundColor: 'rgba(75, 83, 32, 0.2)',
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        border: '1px solid #4B5320',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                    {getFileTypeIcon(fileName)}
                                        {fileName.split('/').pop()}
                                </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const discoverActors = useCallback((files: ProjectFile[]) => {
        if (!files) return [];

        const actorFiles = files.filter(file => {
            if (!file.name.endsWith('.mo')) return false;

            const content = file.content || '';
            const actorPatterns = [
                /^\s*actor\s*\{/m,                    // actor {
                /^\s*actor\s+\w+\s*\{/m,              // actor Name {
                /^\s*actor\s+class\s+\w+.*\{/m        // actor class Name {
            ];

            return actorPatterns.some(pattern => pattern.test(content));
        });

        return actorFiles.map(f => f.name);
    }, []);

    useEffect(() => {
        if (files) {
            const actors = discoverActors(files);
            setAvailableActors(actors);

            const backendCanisters = userCanisters?.filter(c => c.canisterType === 'backend') || [];

            // 🆕 IMMEDIATE AUTO-SELECTION: If single actor + single canister, set them immediately
            if (actors.length === 1 && backendCanisters.length === 1) {
                const autoActor = actors[0];
                const autoCanister = backendCanisters[0].principal.toText();

                console.log(`🎯 AIStudioChat useEffect AUTO-SELECTION: ${autoActor} + ${autoCanister}`);

                setSelectedActor(autoActor);
                setSelectedCanister(autoCanister);

                // Also pre-store the mapping for consistency
                const projectName = project?.name || 'unknown-project';
                const canisterName = getCanisterNameFromActor(autoActor);
                const storageKey = `${projectName}:${canisterName}`;

                // Only store if not already stored
                if (!localStorage.getItem(storageKey)) {
                    localStorage.setItem(storageKey, autoCanister);
                    console.log(`💾 AIStudioChat pre-stored mapping: ${storageKey} = ${autoCanister}`);
                }
            }
        }
    }, [files, userCanisters, discoverActors, project?.name]);

    useEffect(() => {
        if (showActorSelection && availableActors.length > 0) {
            const projectName = project?.name || 'unknown-project';
            const backendCanisters = userCanisters?.filter(c => c.canisterType === 'backend') || [];

            // Try to find a stored mapping for any available actor
            for (const actorFile of availableActors) {
                const canisterName = getCanisterNameFromActor(actorFile);
                const storageKey = `${projectName}:${canisterName}`;
                const storedMapping = localStorage.getItem(storageKey);

                if (storedMapping && storedMapping.trim() !== '') {
                    // Verify the stored canister still exists
                    const canisterExists = backendCanisters.some(c => c.principal.toText() === storedMapping);
                    if (canisterExists) {
                        setSelectedActor(actorFile);
                        setSelectedCanister(storedMapping);
                        console.log(`✅ Pre-populated DeployButton dialog: ${actorFile} → ${storedMapping}`);
                        break; // Use the first found mapping
                    }
                }
            }
        }
    }, [showActorSelection, availableActors, userCanisters, project?.name]);


    const handleDeployAll = async () => {
        if (isDeployingAll || compilationStatus !== 'idle') return;

        // 🔧 FIXED: Use project-specific canister filtering
        const projectBackendCanisters = userCanisters?.filter(c =>
            c.canisterType === 'backend' &&
            project.canisters.some((projectCanister) =>
                projectCanister.toText() === c.principal.toText()
            )
        ) || [];

        console.log(`🚀 AIStudioChat Deploy All - Found ${availableActors.length} actors and ${projectBackendCanisters.length} project backend canisters`);

        // 🆕 ENHANCED: Auto-deploy when single actor + single backend canister
        if (availableActors.length === 1 && projectBackendCanisters.length === 1) {
            const autoMapping = [{
                actorFile: availableActors[0],
                canisterId: projectBackendCanisters[0].principal.toText()
            }];

            console.log(`✅ AIStudioChat Deploy All AUTO-MAPPING:`, autoMapping);

            // Store the mapping before deploying
            const projectName = project?.name || 'unknown-project';
            const canisterName = getCanisterNameFromActor(availableActors[0]);
            const storageKey = `${projectName}:${canisterName}`;

            try {
                localStorage.setItem(storageKey, projectBackendCanisters[0].principal.toText());
                console.log(`💾 AIStudioChat Deploy All auto-stored mapping: ${storageKey} = ${projectBackendCanisters[0].principal.toText()}`);
            } catch (error) {
                console.error('❌ Deploy All auto-mapping storage failed:', error);
            }

            // Execute deployment directly without showing dialog
            await executeDeployAll(autoMapping);
            return;
        }

        // Multiple actors or canisters - show mapping dialog
        if (availableActors.length > 1 || projectBackendCanisters.length > 1) {
            // Initialize mappings with localStorage ONLY (no fallbacks)
            const initialMappings = [];
            const projectName = project?.name || 'unknown-project';

            // Only use stored values, no positional fallbacks
            availableActors.forEach(actorFile => {
                let canisterId = '';

                if (actorFile) {
                    const canisterName = getCanisterNameFromActor(actorFile);
                    const storageKey = `${projectName}:${canisterName}`;
                    const storedMapping = localStorage.getItem(storageKey);

                    if (storedMapping && storedMapping.trim() !== '') {
                        // Verify the stored canister still exists in project
                        const canisterExists = projectBackendCanisters.some(c => c.principal.toText() === storedMapping);
                        if (canisterExists) {
                            canisterId = storedMapping;
                            console.log(`✅ AIStudioChat pre-populated from storage: ${actorFile} → ${canisterId}`);
                        } else {
                            console.log(`⚠️ AIStudioChat stored canister ${storedMapping} no longer exists for ${actorFile}`);
                        }
                    }
                }

                // Leave empty if no stored mapping
                initialMappings.push({
                    actorFile,
                    canisterId
                });
            });

            setActorCanisterMappings(initialMappings);
            setShowDeployAllDialog(true);
            return;
        }

        // Handle edge cases
        if (availableActors.length === 0) {
            onError('No backend actors found to deploy');
            return;
        }

        if (projectBackendCanisters.length === 0) {
            onError('No backend canisters found for this project');
            return;
        }
    };

    const executeDeployAll = async (mappings: ActorCanisterMapping[]) => {
        setIsDeployingAll(true);
        setShowDeployAllDialog(false);

        try {
            const store = useIDEStore.getState();
            const originalActiveType = store.activeType;
            const originalActiveFile = store.activeFile;

            // Clear any previous deployment errors
            store.setDeploymentError(null);

            // Filter out empty mappings and get valid backend deployments
            const validMappings = mappings.filter(m => m.actorFile && m.canisterId);

            console.log(`🚀 Deploy All: Starting ${validMappings.length} backend deployments`);

            // Phase 1: Deploy each backend actor sequentially
            for (let i = 0; i < validMappings.length; i++) {
                const mapping = validMappings[i];
                const actorName = mapping.actorFile.split('/').pop() || mapping.actorFile;

                setDeployStep(`🟣 Deploying Backend ${i + 1}/${validMappings.length}: ${actorName}`);
                console.log(`🚀 Phase 1.${i + 1}: Deploying ${mapping.actorFile} to ${mapping.canisterId}`);

                // 🆕 SOLUTION 1: Store mapping in localStorage BEFORE deploying
                const projectName = project?.name || 'unknown-project';
                const canisterName = getCanisterNameFromActor(mapping.actorFile);
                const storageKey = `${projectName}:${canisterName}`;

                console.log(`💾 Deploy All storing mapping:`);
                console.log(`  - Project: ${projectName}`);
                console.log(`  - Actor: ${mapping.actorFile}`);
                console.log(`  - Canister Name: ${canisterName}`);
                console.log(`  - Storage Key: ${storageKey}`);
                console.log(`  - Storage Value: ${mapping.canisterId}`);

                try {
                    localStorage.setItem(storageKey, mapping.canisterId);
                    console.log(`💾 Deploy All stored: ${storageKey} = ${mapping.canisterId}`);

                    // Verify storage immediately
                    const verification = localStorage.getItem(storageKey);
                    if (verification !== mapping.canisterId) {
                        console.error(`❌ Storage verification failed for ${storageKey}`);
                        console.error(`  Expected: ${mapping.canisterId}`);
                        console.error(`  Got: ${verification}`);
                    } else {
                        console.log(`✅ Storage verification successful for ${storageKey}`);
                    }
                } catch (storageError) {
                    console.error(`❌ localStorage.setItem failed for ${storageKey}:`, storageError);
                }

                // Set the active file to the current actor
                store.setActiveFile(mapping.actorFile);
                store.setActiveType('backend');

                // Deploy this specific backend
                await store.deploy(null, null, 'reinstall', 'icpstudio');

                // Wait and check for errors
                await new Promise(resolve => setTimeout(resolve, 1500));

                const currentDeploymentError = useIDEStore.getState().deploymentError;
                if (currentDeploymentError) {
                    console.error(`❌ Phase 1.${i + 1}: Backend deployment failed:`, currentDeploymentError);

                    setDeployStep(`❌ Backend ${actorName} deployment failed - stopping deploy all`);
                    onError(`Backend deployment failed for ${actorName}: ${currentDeploymentError}`);

                    // Restore original state
                    store.setActiveFile(originalActiveFile);
                    store.setActiveType(originalActiveType);

                    setTimeout(() => {
                        setDeployStep('');
                        setIsDeployingAll(false);
                    }, 5000);

                    return; // Exit early on any backend failure
                }

                console.log(`✅ Phase 1.${i + 1}: Backend ${actorName} deployment completed successfully`);

                // Brief pause between backend deployments
                if (i < validMappings.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }

            // Wait before proceeding to frontend
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Phase 2: Deploy Frontend (only if all backends succeeded)
            setDeployStep('🔵 Deploying Frontend...');
            console.log('🚀 Phase 2: Starting frontend deployment...');

            // Clear any deployment errors before frontend deploy
            store.setDeploymentError(null);
            store.setActiveType('frontend');

            await store.deploy(null, null, 'reinstall', 'icpstudio');

            // Check for frontend deployment errors
            await new Promise(resolve => setTimeout(resolve, 1500));
            const frontendDeploymentError = useIDEStore.getState().deploymentError;

            if (frontendDeploymentError) {
                console.error('❌ Phase 2: Frontend deployment failed:', frontendDeploymentError);
                setDeployStep('❌ Frontend deployment failed (all backends were successful)');
                onError(`Frontend deployment failed: ${frontendDeploymentError}. All backend deployments were successful.`);
            } else {
                console.log('✅ Phase 2: Frontend deployment completed');
                setDeployStep('✅ All Deployments Complete!');
                onSuccess(`🎉 Successfully deployed ${validMappings.length} backend${validMappings.length > 1 ? 's' : ''} and frontend!`);
            }

            // Restore original state
            store.setActiveFile(originalActiveFile);
            store.setActiveType(originalActiveType);

            setTimeout(() => {
                setDeployStep('');
                setIsDeployingAll(false);
            }, 4000);

        } catch (error: any) {
            console.error('❌ Deploy All failed with unexpected error:', error);
            const errorMessage = error?.message || String(error);
            setDeployStep(`❌ Deployment failed: ${errorMessage}`);
            onError(`Deploy All failed: ${errorMessage}`);

            // Restore state
            const store = useIDEStore.getState();
            store.setActiveType('backend');

            setTimeout(() => {
                setDeployStep('');
                setIsDeployingAll(false);
            }, 5000);
        }
    };


    const executeBackendDeploy = (deploymentId: any, installMode: string, projectType: string, actorFile: string, canisterId: string) => {
        console.log('🔥 AIStudioChat executeBackendDeploy CALLED');
        console.log('  - actorFile:', actorFile);
        console.log('  - canisterId:', canisterId);

        // Check if parameters are valid
        if (!actorFile) {
            console.error('❌ AIStudioChat actorFile is empty/undefined');
            return;
        }
        if (!canisterId) {
            console.error('❌ AIStudioChat canisterId is empty/undefined');
            return;
        }

        console.log(`🎯 AIStudioChat executeBackendDeploy called with actor: ${actorFile}`);

        // Store canister mapping in localStorage
        const projectName = project?.name || 'unknown-project';
        const canisterName = getCanisterNameFromActor(actorFile);
        const storageKey = `${projectName}:${canisterName}`;

        try {
            localStorage.setItem(storageKey, canisterId);
            console.log(`💾 AIStudioChat STORED mapping: ${storageKey} = ${canisterId}`);

            // Verify storage immediately
            const verification = localStorage.getItem(storageKey);
            if (verification !== canisterId) {
                console.error('❌ AIStudioChat storage verification failed!');
                console.error(`  Expected: ${canisterId}`);
                console.error(`  Got: ${verification}`);
            } else {
                console.log('✅ AIStudioChat storage verification successful');
            }
        } catch (error) {
            console.error('❌ AIStudioChat localStorage.setItem failed:', error);
            onError?.(`Failed to store canister mapping: ${error}`);
        }

        // Set the active file and switch to backend type
        const store = useIDEStore.getState();
        console.log(`🟣 AIStudioChat: BEFORE - activeType: '${store.activeType}', activeFile: '${store.activeFile}'`);

        store.setActiveFile(actorFile);
        store.setActiveType('backend');

        console.log(`🟣 AIStudioChat: AFTER - switched to backend mode, activeFile: '${actorFile}'`);

        const isMainMo = actorFile && (actorFile.endsWith('/main.mo') || actorFile.endsWith('/Main.mo'));

        if (!isMainMo) {
            // FIXED: Pass canisterId as 5th parameter to deploy
            console.log(`🚀 AIStudioChat: Deploying NON-main.mo backend file: ${actorFile} to canister: ${canisterId}`);
            deploy(deploymentId, null, installMode, projectType, canisterId, actorFile);
            return;
        }

        // Check if it's a class-based actor for main.mo files
        const actorFileObj = files.find(f => f.name === actorFile);
        const content = actorFileObj?.content || '';

        const classMatch = content.match(/actor\s+class\s+(\w+)\s*\((.*?)\)/);
        if (classMatch) {
            console.log(`🎯 AIStudioChat: Found actor class ${classMatch[1]} - showing init args dialog`);
            setActorClassInfo({
                className: classMatch[1],
                initParams: classMatch[2]
            });
            setPendingDeploymentId(deploymentId);
            setPendingDeploymentMode(installMode);
            setPendingProjectType(projectType);
            setPendingCanisterId(canisterId); // FIXED: Store the canister ID for later use
            setShowClassInitArgsDialog(true);
        } else {
            // FIXED: Pass canisterId as 5th parameter to deploy
            console.log(`🚀 AIStudioChat: Deploying main.mo backend file (no class): ${actorFile} to canister: ${canisterId}`);
            deploy(deploymentId, null, installMode, projectType, canisterId, selectedActor);
        }
    };


    const getCanisterNameFromActor = (actorFile: string): string => {
        console.log('🔍 getCanisterNameFromActor input:', actorFile);

        // Extract name from path like "MyProject/src/backend/src/main.mo"
        const fileName = actorFile.split('/').pop() || actorFile;
        console.log('  - File name:', fileName);

        // Remove extension and normalize
        const baseName = fileName.replace('.mo', '');
        console.log('  - Base name:', baseName);

        // Convert to canister naming convention
        const result = baseName.toLowerCase() === 'main' ? 'main' : baseName.toLowerCase();
        console.log('  - Final canister name:', result);

        return result;
    };

    const handleFixDeploymentError = () => {
        if (!deploymentError) return;

        // Disable no-code mode to ensure we get an actual fix
        setNoCodeMode(false);

        // Create the fix prompt
        const fixPrompt = `Please fix the following error that happened on last deploy:

${deploymentError}

Please analyze the error and provide a complete solution with updated code files to resolve this deployment issue.`;

        // Send the message directly
        sendMessage(fixPrompt);

        // 🆕 NEW: Clear the deployment error since we're now actively fixing it
        console.log('🔧 AIStudio: Clearing deployment error since AI is now handling it');
        useIDEStore.getState().setDeploymentError(null);
    };


    const renderActorSelectionDialog = () => {
        if (!showActorSelection) return null;

        const isDeployAll = deploymentContext === 'deployAll';
        // 🔧 FIXED: Filter canisters by project (matching deployment handler logic)
        const backendCanisters = userCanisters?.filter(c =>
            c.canisterType === 'backend' &&
            project.canisters.some((projectCanister) =>
                projectCanister.toText() === c.principal.toText()
            )
        ) || [];

        // 🔧 FIXED: Determine what to show in dialog
        const shouldShowActorSelection = availableActors.length > 1;
        const shouldShowCanisterSelection = backendCanisters.length > 1;

        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
            }}>
                <div style={{
                    backgroundColor: '#fdf6e3',
                    borderRadius: '8px',
                    padding: '24px',
                    width: '450px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                }}>
                    <h3 style={{
                        margin: '0 0 16px 0',
                        color: '#5C4033',
                        fontSize: '18px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        🎯 {shouldShowActorSelection && shouldShowCanisterSelection
                        ? 'Select Backend Actor & Canister'
                        : shouldShowActorSelection
                            ? 'Select Backend Actor'
                            : 'Select Target Canister'
                    }
                    </h3>

                    <p style={{color: '#8B4513', fontSize: '14px', marginBottom: '20px'}}>
                        {shouldShowActorSelection && shouldShowCanisterSelection
                            ? 'Multiple actors and canisters found. Please select both.'
                            : shouldShowActorSelection
                                ? 'Multiple backend actors found. Please select which one to deploy.'
                                : 'Multiple backend canisters found. Please select the target canister.'
                        }
                    </p>

                    {/* Actor Selection - only show if multiple actors */}
                    {shouldShowActorSelection && (
                        <div style={{marginBottom: '16px'}}>
                            <label style={{display: 'block', marginBottom: '8px', color: '#5C4033', fontWeight: '500'}}>
                                Actor to Deploy:
                            </label>
                            <select
                                value={selectedActor}
                                onChange={(e) => setSelectedActor(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    border: '1px solid #8B4513',
                                    backgroundColor: '#fff',
                                    fontSize: '14px'
                                }}
                            >
                                <option value="">Select an actor...</option>
                                {availableActors.map(actor => (
                                    <option key={actor} value={actor}>{actor}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Canister Selection - only show if multiple canisters */}
                    {shouldShowCanisterSelection && (
                        <div style={{marginBottom: '24px'}}>
                            <label style={{display: 'block', marginBottom: '8px', color: '#5C4033', fontWeight: '500'}}>
                                Target Canister:
                            </label>
                            <select
                                value={selectedCanister}
                                onChange={(e) => setSelectedCanister(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    border: '1px solid #8B4513',
                                    backgroundColor: '#fff',
                                    fontSize: '14px'
                                }}
                            >
                                <option value="">Select a canister...</option>
                                {backendCanisters.map(canister => (
                                    <option key={canister.principal.toText()} value={canister.principal.toText()}>
                                        {canister.name || canister.principal.toText()}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                        <button
                            onClick={() => {
                                setShowActorSelection(false);
                                setDeploymentContext(null);
                            }}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#8B4513',
                                color: '#FFF5EE',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: '500'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                // Validate selections based on what's shown
                                const actorValid = !shouldShowActorSelection || selectedActor;
                                const canisterValid = !shouldShowCanisterSelection || selectedCanister;

                                if (actorValid && canisterValid) {
                                    setShowActorSelection(false);

                                    // Use selected or pre-filled values
                                    const finalActor = selectedActor || availableActors[0];
                                    const finalCanister = selectedCanister || backendCanisters[0].principal.toText();

                                    // Store mapping in localStorage (ALWAYS store, regardless of count)
                                    const projectName = project?.name || 'unknown-project';
                                    const canisterName = getCanisterNameFromActor(finalActor);
                                    const storageKey = `${projectName}:${canisterName}`;

                                    console.log('🎯 Dialog: Storing mapping (ALWAYS store)');
                                    console.log('  - Project:', projectName);
                                    console.log('  - Actor:', finalActor);
                                    console.log('  - Canister Name:', canisterName);
                                    console.log('  - Storage Key:', storageKey);
                                    console.log('  - Storage Value:', finalCanister);

                                    try {
                                        localStorage.setItem(storageKey, finalCanister);
                                        console.log(`💾 Dialog STORED mapping: ${storageKey} = ${finalCanister}`);

                                        // Verify storage
                                        const verification = localStorage.getItem(storageKey);
                                        if (verification !== finalCanister) {
                                            console.error('❌ Dialog storage verification failed!');
                                        } else {
                                            console.log('✅ Dialog storage verification successful');
                                        }
                                    } catch (error) {
                                        console.error('❌ Dialog localStorage.setItem failed:', error);
                                    }

                                    // Deploy backend
                                    executeBackendDeploy(null, 'upgrade', 'icpstudio', finalActor, finalCanister);
                                    setDeploymentContext(null);
                                }
                            }}
                            disabled={
                                (shouldShowActorSelection && !selectedActor) ||
                                (shouldShowCanisterSelection && !selectedCanister)
                            }
                            style={{
                                padding: '10px 20px',
                                backgroundColor:
                                    (shouldShowActorSelection && !selectedActor) ||
                                    (shouldShowCanisterSelection && !selectedCanister)
                                        ? '#ccc'
                                        : '#1976d2',
                                color: '#FFF5EE',
                                border: 'none',
                                borderRadius: '4px',
                                cursor:
                                    (shouldShowActorSelection && !selectedActor) ||
                                    (shouldShowCanisterSelection && !selectedCanister)
                                        ? 'not-allowed'
                                        : 'pointer',
                                fontWeight: '500'
                            }}
                        >
                            🟣 Deploy Backend
                        </button>
                    </div>
                </div>
            </div>
        );
    };


    const renderDeploySection = () => {
        return (
            <div style={{
                padding: '10px 12px',
                backgroundColor: '#f0f7ff',
                border: '1px solid #c3dafe',
                borderRadius: '6px',
                marginBottom: '8px'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        {/* Deploy All Button */}
                        <button
                            onClick={handleDeployAll}
                            disabled={isDeployingAll || compilationStatus !== 'idle' || !compilerReady}
                            style={{
                                padding: '6px 12px',
                                backgroundColor: (isDeployingAll || compilationStatus !== 'idle' || !compilerReady)
                                    ? '#9B5523'
                                    : '#1976d2',
                                color: '#FFF5EE',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: (isDeployingAll || compilationStatus !== 'idle' || !compilerReady)
                                    ? 'not-allowed'
                                    : 'pointer',
                                fontWeight: '600',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                if (!isDeployingAll && compilationStatus === 'idle' && compilerReady) {
                                    e.currentTarget.style.backgroundColor = '#1565c0';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isDeployingAll && compilationStatus === 'idle' && compilerReady) {
                                    e.currentTarget.style.backgroundColor = '#1976d2';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }
                            }}
                        >
                            {isDeployingAll ? (
                                <>
                                    <div style={{
                                        width: '14px',
                                        height: '14px',
                                        border: '2px solid rgba(255,255,255,0.3)',
                                        borderTopColor: '#FFF',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                    }}/>
                                    Deploying...
                                </>
                            ) : (
                                <>🚀 Deploy All</>
                            )}
                        </button>

                        {/* Show deployment step if deploying */}
                        {deployStep && (
                            <span style={{
                                fontSize: '12px',
                                color: isDeployingAll ? '#1976d2' : '#4caf50',
                                fontWeight: '500'
                            }}>
                            {deployStep}
                        </span>
                        )}
                    </div>

                    <div style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center'
                    }}>
                        {compilationStatus !== 'idle' && (
                            <div style={{
                                fontSize: '11px',
                                color: '#d97706',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}>
                                <div style={{
                                    width: '10px',
                                    height: '10px',
                                    border: '2px solid rgba(217, 119, 6, 0.3)',
                                    borderTopColor: '#d97706',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }}/>
                                Compiling...
                            </div>
                        )}

                        {!compilerReady && (
                            <div style={{
                                fontSize: '11px',
                                color: '#dc2626'
                            }}>
                                Compiler not ready
                            </div>
                        )}

                        {/* Backend Deploy Button */}
                        <button
                            onClick={() => handleDeployWithClassCheck(null, 'reinstall', 'icpstudio')}
                            disabled={compilationStatus !== 'idle' || !compilerReady || isDeployingAll}
                            style={{
                                padding: '6px 12px',
                                backgroundColor: (compilationStatus !== 'idle' || !compilerReady || isDeployingAll) ? '#9B5523' : '#5C4033',
                                color: '#FFF5EE',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: (compilationStatus !== 'idle' || !compilerReady || isDeployingAll) ? 'not-allowed' : 'pointer',
                                fontWeight: '600',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.2s ease',
                                opacity: isDeployingAll ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => {
                                if (compilationStatus === 'idle' && compilerReady && !isDeployingAll) {
                                    e.currentTarget.style.backgroundColor = '#4a3628';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (compilationStatus === 'idle' && compilerReady && !isDeployingAll) {
                                    e.currentTarget.style.backgroundColor = '#5C4033';
                                }
                            }}
                        >
                            🟣 Deploy Backend
                        </button>

                        {/* Frontend Deploy Button */}
                        <button
                            onClick={handleFrontendDeploy}
                            disabled={compilationStatus !== 'idle' || isDeployingAll}
                            style={{
                                padding: '6px 12px',
                                backgroundColor: (compilationStatus !== 'idle' || isDeployingAll) ? '#9B5523' : '#5C4033',
                                color: '#FFF5EE',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: (compilationStatus !== 'idle' || isDeployingAll) ? 'not-allowed' : 'pointer',
                                fontWeight: '600',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.2s ease',
                                opacity: isDeployingAll ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => {
                                if (compilationStatus === 'idle' && !isDeployingAll) {
                                    e.currentTarget.style.backgroundColor = '#4a3628';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (compilationStatus === 'idle' && !isDeployingAll) {
                                    e.currentTarget.style.backgroundColor = '#5C4033';
                                }
                            }}
                        >
                            🔵 Deploy Frontend
                        </button>
                    </div>
                </div>

                {/* Enhanced Error Display with Fix This Button */}
                {deploymentError && (
                    <div style={{
                        marginTop: '8px',
                        padding: '12px',
                        backgroundColor: '#fee2e2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#dc2626'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: '12px'
                        }}>
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    fontWeight: '600',
                                    marginBottom: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    ⚠️ Deployment Error:
                                </div>
                                <div style={{
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontFamily: 'monospace',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    maxHeight: '150px',
                                    overflowY: 'auto'
                                }}>
                                    {deploymentError}
                                </div>
                            </div>

                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px',
                                alignItems: 'flex-end'
                            }}>
                                <button
                                    onClick={handleFixDeploymentError}
                                    disabled={isLoading || isStreaming}
                                    style={{
                                        padding: '8px 14px',
                                        backgroundColor: isLoading || isStreaming ? '#9B5523' : '#dc2626',
                                        color: '#FFF',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: isLoading || isStreaming ? 'not-allowed' : 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        whiteSpace: 'nowrap',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isLoading && !isStreaming) {
                                            e.currentTarget.style.backgroundColor = '#b91c1c';
                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isLoading && !isStreaming) {
                                            e.currentTarget.style.backgroundColor = '#dc2626';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                        }
                                    }}
                                >
                                    {isLoading || isStreaming ? (
                                        <>
                                            <div style={{
                                                width: '12px',
                                                height: '12px',
                                                border: '2px solid rgba(255,255,255,0.3)',
                                                borderTopColor: '#FFF',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }}/>
                                            Fixing...
                                        </>
                                    ) : (
                                        <>🔧 Fix This</>
                                    )}
                                </button>

                                <button
                                    onClick={() => useIDEStore.getState().setDeploymentError(null)}
                                    style={{
                                        padding: '4px 8px',
                                        background: 'transparent',
                                        border: '1px solid #dc2626',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        color: '#dc2626',
                                        cursor: 'pointer',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    × Dismiss
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };


    const renderDeployAllDialog = () => {
        if (!showDeployAllDialog) return null;

        // 🔧 FIXED: Filter canisters by project (matching deployment handler logic)
        const backendCanisters = userCanisters?.filter(c =>
            c.canisterType === 'backend' &&
            project.canisters.some((projectCanister) =>
                projectCanister.toText() === c.principal.toText()
            )
        ) || [];

        const addMapping = () => {
            setActorCanisterMappings(prev => [...prev, {actorFile: '', canisterId: ''}]);
        };

        const removeMapping = (index: number) => {
            setActorCanisterMappings(prev => prev.filter((_, i) => i !== index));
        };

        const updateMapping = (index: number, field: 'actorFile' | 'canisterId', value: string) => {
            setActorCanisterMappings(prev => prev.map((mapping, i) =>
                i === index ? {...mapping, [field]: value} : mapping
            ));
        };

        const validMappings = actorCanisterMappings.filter(m => m.actorFile && m.canisterId);
        const canProceed = validMappings.length > 0;

        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
            }}>
                <div style={{
                    backgroundColor: '#fdf6e3',
                    borderRadius: '8px',
                    padding: '24px',
                    width: '600px',
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                }}>
                    <h3 style={{margin: '0 0 16px 0', color: '#5C4033', fontSize: '18px', fontWeight: '600'}}>
                        🚀 Deploy All - Actor to Canister Mapping
                    </h3>

                    <p style={{color: '#8B4513', fontSize: '14px', marginBottom: '20px', lineHeight: '1.4'}}>
                        Map each backend actor to a specific canister. All backends will be deployed sequentially,
                        then the frontend will be deployed last.
                    </p>

                    <div style={{marginBottom: '20px'}}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '12px'
                        }}>
                            <h4 style={{margin: 0, color: '#5C4033', fontSize: '14px', fontWeight: '600'}}>
                                Backend Deployments:
                            </h4>
                            <button
                                onClick={addMapping}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#4caf50',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                }}
                            >
                                + Add Mapping
                            </button>
                        </div>

                        {actorCanisterMappings.map((mapping, index) => (
                            <div key={index} style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                marginBottom: '12px',
                                padding: '12px',
                                backgroundColor: '#f3e5d8',
                                borderRadius: '6px',
                                border: '1px solid #e5d8c8'
                            }}>
                        <span style={{
                            minWidth: '20px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#5C4033'
                        }}>
                            {index + 1}.
                        </span>

                                <div style={{flex: 1}}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '4px',
                                        fontSize: '11px',
                                        color: '#8B4513',
                                        fontWeight: '500'
                                    }}>
                                        Actor:
                                    </label>
                                    <select
                                        value={mapping.actorFile}
                                        onChange={(e) => updateMapping(index, 'actorFile', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            borderRadius: '4px',
                                            border: '1px solid #8B4513',
                                            backgroundColor: '#fff',
                                            fontSize: '12px'
                                        }}
                                    >
                                        <option value="">Select actor...</option>
                                        {availableActors.map(actor => (
                                            <option key={actor} value={actor}>
                                                {actor.split('/').pop()}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div style={{flex: 1}}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '4px',
                                        fontSize: '11px',
                                        color: '#8B4513',
                                        fontWeight: '500'
                                    }}>
                                        → Canister:
                                    </label>
                                    <select
                                        value={mapping.canisterId}
                                        onChange={(e) => updateMapping(index, 'canisterId', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            borderRadius: '4px',
                                            border: '1px solid #8B4513',
                                            backgroundColor: '#fff',
                                            fontSize: '12px'
                                        }}
                                    >
                                        <option value="">Select canister...</option>
                                        {backendCanisters.map(canister => (
                                            <option key={canister.principal.toText()}
                                                    value={canister.principal.toText()}>
                                                {canister.name || canister.principal.toText().substring(0, 8) + '...'}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {actorCanisterMappings.length > 1 && (
                                    <button
                                        onClick={() => removeMapping(index)}
                                        style={{
                                            padding: '8px',
                                            backgroundColor: '#dc2626',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            minWidth: '30px'
                                        }}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        ))}

                        {actorCanisterMappings.length === 0 && (
                            <div style={{
                                padding: '20px',
                                textAlign: 'center',
                                color: '#8B4513',
                                fontSize: '14px',
                                fontStyle: 'italic',
                                backgroundColor: '#f9f9f9',
                                borderRadius: '6px',
                                border: '1px dashed #ccc'
                            }}>
                                No mappings configured. Click "Add Mapping" to start.
                            </div>
                        )}
                    </div>

                    {validMappings.length > 0 && (
                        <div style={{
                            marginBottom: '20px',
                            padding: '12px',
                            backgroundColor: '#e8f5e9',
                            border: '1px solid #4caf50',
                            borderRadius: '6px'
                        }}>
                            <h5 style={{margin: '0 0 8px 0', color: '#2e7d32', fontSize: '13px', fontWeight: '600'}}>
                                Deployment Plan:
                            </h5>
                            <div style={{fontSize: '12px', color: '#2e7d32', lineHeight: '1.4'}}>
                                {validMappings.map((mapping, index) => (
                                    <div key={index}>
                                        {index + 1}.
                                        Deploy <strong>{mapping.actorFile.split('/').pop()}</strong> to{' '}
                                        <strong>{backendCanisters.find(c => c.principal.toText() === mapping.canisterId)?.name ||
                                            mapping.canisterId.substring(0, 8) + '...'}</strong>
                                    </div>
                                ))}
                                <div style={{marginTop: '4px', fontWeight: '600'}}>
                                    {validMappings.length + 1}. Deploy Frontend
                                </div>
                            </div>
                        </div>
                    )}

                    <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                        <button
                            onClick={() => {
                                setShowDeployAllDialog(false);
                                setActorCanisterMappings([]);
                            }}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#8B4513',
                                color: '#FFF5EE',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: '500'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => executeDeployAll(actorCanisterMappings)}
                            disabled={!canProceed}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: canProceed ? '#1976d2' : '#ccc',
                                color: '#FFF5EE',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: canProceed ? 'pointer' : 'not-allowed',
                                fontWeight: '500'
                            }}
                        >
                            🚀 Deploy All ({validMappings.length} backend{validMappings.length !== 1 ? 's' : ''} +
                            frontend)
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderClassInitArgsDialog = () => {
        if (!showClassInitArgsDialog) return null;

        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
            }}>
                <div style={{
                    backgroundColor: '#fdf6e3',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '2px solid #8B4513',
                    minWidth: '400px',
                    maxWidth: '600px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                }}>
                    <h3 style={{
                        margin: '0 0 16px 0',
                        color: '#5C4033',
                        fontSize: '18px',
                        fontWeight: '600'
                    }}>
                        🎯 Actor Class Initialization
                    </h3>

                    <div style={{
                        margin: '0 0 16px 0',
                        padding: '12px',
                        backgroundColor: '#f3e5d8',
                        borderRadius: '6px',
                        border: '1px solid #e5d8c8'
                    }}>
                        <p style={{
                            margin: '0 0 8px 0',
                            color: '#8B4513',
                            fontSize: '14px'
                        }}>
                            <strong>Class:</strong> {actorClassInfo.className}
                        </p>
                        <p style={{
                            margin: 0,
                            color: '#8B4513',
                            fontSize: '14px'
                        }}>
                            <strong>Parameters:</strong> {actorClassInfo.initParams || 'None'}
                        </p>
                    </div>

                    <div style={{marginBottom: '20px'}}>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            color: '#5C4033',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}>
                            Initialization Arguments:
                        </label>
                        <input
                            type="text"
                            value={initArgsValue}
                            onChange={(e) => setInitArgsValue(e.target.value)}
                            placeholder="Enter init args (e.g., null, 100, &quot;hello&quot;)"
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '1px solid #8B4513',
                                borderRadius: '6px',
                                fontSize: '14px',
                                boxSizing: 'border-box',
                                backgroundColor: '#fff'
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleClassInitArgsSubmit(initArgsValue);
                                }
                            }}
                        />
                        <div style={{
                            marginTop: '8px',
                            fontSize: '12px',
                            color: '#8B4513'
                        }}>
                            💡 Tip: Use Motoko syntax (e.g., null, 42, "text", true)
                        </div>
                    </div>

                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'flex-end'
                    }}>
                        <button
                            onClick={() => {
                                setShowClassInitArgsDialog(false);
                                setPendingDeploymentId(null);
                                setPendingDeploymentMode(null);
                                setPendingProjectType('icpstudio');
                                setInitArgsValue('');
                            }}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: 'transparent',
                                color: '#8B4513',
                                border: '1px solid #8B4513',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                fontSize: '14px'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                handleClassInitArgsSubmit(initArgsValue);
                            }}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#5C4033',
                                color: '#FFF5EE',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                fontSize: '14px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            🚀 Deploy
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const contextSummary = getContextSummary();
    const quickSuggestions = generateQuickSuggestions();

    return (
        <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fdf6e3',
            position: 'relative'
        }}>
            {renderApplyProgress()}
            {renderClassInitArgsDialog()}
            {renderActorSelectionDialog()}
            {renderDeployAllDialog()}

            {showFrontendMappingDialog && (
                <FrontendMappingDialog
                    isOpen={showFrontendMappingDialog}
                    onClose={() => setShowFrontendMappingDialog(false)}
                    onProceedWithMappings={handleFrontendMappingComplete}
                    availableActors={availableActors}
                    userCanisters={userCanisters}
                    project={project}
                    getCanisterNameFromActor={getCanisterNameFromActor}
                />
            )}

            {/* Balanced Header */}
            <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid #8B4513',
                backgroundColor: '#f3e5d8'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                }}>
                    <div>
                        <h2 style={{
                            margin: 0,
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#5C4033',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            💬 ICP Studio Chat
                            {isStreaming && (
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    border: '2px solid rgba(92, 64, 51, 0.3)',
                                    borderTopColor: '#5C4033',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }}/>
                            )}
                        </h2>
                        <p style={{
                            margin: '4px 0 0 0',
                            fontSize: '13px',
                            color: '#8B4513'
                        }}>
                            Enhanced context-aware AI assistant with deployment capabilities
                        </p>
                    </div>

                    {messages.length > 0 && (
                        <button
                            onClick={clearConversation}
                            style={{
                                padding: '6px 12px',
                                backgroundColor: 'transparent',
                                border: '1px solid #8B4513',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                color: '#8B4513',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(139, 69, 19, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            🗑️ Clear Chat
                        </button>
                    )}
                </div>

                {/* Enhanced Context Status - Compact but readable */}
                {contextSummary.hasEnhancedContext && (
                    <div style={{
                        padding: '8px 12px',
                        backgroundColor: '#e8f5e9',
                        border: '1px solid #4caf50',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#2e7d32',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <span style={{fontWeight: '600'}}>✓ Enhanced Context:</span>
                        {contextSummary.enabledRules > 0 && <span>📋 {contextSummary.enabledRules} rules</span>}
                        {contextSummary.activeDocs > 0 && <span>📚 {contextSummary.activeDocs} docs</span>}
                        {contextSummary.activeGitHub > 0 && <span>🔗 {contextSummary.activeGitHub} GitHub</span>}
                        {contextSummary.connectedMcp > 0 && <span>🔌 {contextSummary.connectedMcp} MCP</span>}
                    </div>
                )}

                {/* File Context Selector */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    position: 'relative'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '13px',
                        color: '#5C4033'
                    }}>
                        <span style={{fontWeight: '600'}}>📁 File Context:</span>
                        <button
                            onClick={() => setShowFileSelector(!showFileSelector)}
                            style={{
                                background: 'none',
                                border: '1px solid #8B4513',
                                borderRadius: '5px',
                                padding: '5px 10px',
                                fontSize: '12px',
                                color: '#5C4033',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(139, 69, 19, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            {selectedFiles.length > 0 ? `${selectedFiles.length} files selected` : 'No files selected'}
                            <span style={{fontSize: '10px'}}>{showFileSelector ? '▲' : '▼'}</span>
                        </button>
                    </div>

                    {renderNoCodeToggle()}

                    {selectedFiles.length > 0 && (
                        <span style={{
                            fontSize: '11px',
                            color: '#4B5320',
                            backgroundColor: 'rgba(75, 83, 32, 0.1)',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontWeight: '500'
                        }}>
                            Multi-file context active
                        </span>
                    )}

                    {renderFileSelector()}
                </div>
            </div>

            {/* Deploy Section */}
            {renderDeploySection()}

            {/* Messages Area */}
            <div
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: messages.length === 0 ? '40px 24px' : '24px', // More padding when empty
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    minHeight: 0 // Important: allows flex child to shrink
                }}

                onScroll={(e) => { // 🆕 ADD THIS ENTIRE onScroll HANDLER
                    const target = e.target as HTMLDivElement;
                    const isAtBottom = Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) < 5;
                    setAutoScroll(isAtBottom);

                    console.log('Scroll detection:', {
                        scrollHeight: target.scrollHeight,
                        scrollTop: target.scrollTop,
                        clientHeight: target.clientHeight,
                        isAtBottom: isAtBottom
                    });
                }}
            >
                {messages.length === 0 && !isStreaming && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 1, // Take up remaining space
                        textAlign: 'center',
                        color: '#8B4513',
                        fontSize: '16px',
                        padding: '40px 20px',
                        minHeight: '400px' // Ensure minimum height for centering
                    }}>
                        <div style={{
                            fontSize: '48px',
                            marginBottom: '20px'
                        }}>
                            🤖✨
                        </div>
                        <div style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#5C4033',
                            marginBottom: '12px'
                        }}>
                            Welcome to AI Studio Chat!
                        </div>
                        <div style={{
                            marginBottom: '32px',
                            lineHeight: '1.5',
                            maxWidth: '500px' // Constrain text width
                        }}>
                            Start a conversation with the ICP Studio AI assistant.<br/>
                            Remember to deploy the front end & the backend to see your AI changes on chain !!
                        </div>

                        {/* Quick Suggestions */}
                        <div style={{
                            textAlign: 'left',
                            maxWidth: '800px', // Increased max width
                            width: '100%'
                        }}>
                            <h4 style={{
                                color: '#5C4033',
                                fontSize: '16px',
                                fontWeight: '600',
                                marginBottom: '16px',
                                textAlign: 'center'
                            }}>
                                💡 Quick Suggestions
                            </h4>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', // Wider min width
                                gap: '12px',
                                justifyContent: 'center'
                            }}>
                                {quickSuggestions.map((suggestion, index) => (
                                    <button
                                        key={index}
                                        onClick={() => sendMessage(suggestion)}
                                        disabled={isLoading}
                                        style={{
                                            padding: '16px 20px', // More padding
                                            backgroundColor: '#fff',
                                            border: '1px solid #e5d8c8',
                                            borderRadius: '8px',
                                            cursor: isLoading ? 'not-allowed' : 'pointer',
                                            textAlign: 'left',
                                            fontSize: '14px', // Slightly larger font
                                            color: '#5C4033',
                                            lineHeight: '1.4',
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                            minHeight: '80px', // Consistent height
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isLoading) {
                                                e.currentTarget.style.backgroundColor = '#f3e5d8';
                                                e.currentTarget.style.borderColor = '#8B4513';
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isLoading) {
                                                e.currentTarget.style.backgroundColor = '#fff';
                                                e.currentTarget.style.borderColor = '#e5d8c8';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                            }
                                        }}
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}


                {messages.map(renderMessage)}

                {isStreaming && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        alignSelf: 'flex-start',
                        maxWidth: '75%',
                        marginBottom: '20px'
                    }}>
                        <div style={{
                            fontSize: '12px',
                            color: '#8B4513',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            🤖 AI Assistant
                            <span style={{
                                fontSize: '10px',
                                backgroundColor: 'rgba(139, 69, 19, 0.1)',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                color: '#4B5320'
                            }}>
                Streaming...
            </span>
                        </div>
                        <div style={{
                            padding: '16px 20px',
                            borderRadius: '16px 16px 16px 4px',
                            backgroundColor: '#f3e5d8',
                            color: '#5C4033',
                            fontSize: '14px',
                            lineHeight: '1.5',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            border: '1px solid #e5d8c8',
                            position: 'relative'
                        }}>
                            {streamingContent}
                            <span style={{
                                opacity: 0.7,
                                animation: 'blink 1s infinite',
                                fontSize: '16px',
                                color: '#4B5320'
                            }}>▋</span>
                        </div>
                    </div>
                )}

                {isLoading && !isStreaming && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        color: '#8B4513',
                        fontSize: '14px',
                        padding: '20px',
                        justifyContent: 'center'
                    }}>
                        <div style={{
                            width: '20px',
                            height: '20px',
                            border: '3px solid rgba(139, 69, 19, 0.3)',
                            borderTopColor: '#8B4513',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}/>
                        <span>AI is analyzing with enhanced context...</span>
                    </div>
                )}

                <div ref={messagesEndRef}/>
            </div>

            {/* Compact Input Area */}
            <div style={{
                padding: '12px 16px',
                borderTop: '1px solid #8B4513',
                backgroundColor: '#f3e5d8'
            }}>
                <div style={{
                    position: 'relative',
                    marginBottom: '8px'
                }}>
                    <textarea
                        ref={inputRef}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Ask anything about your code... Enhanced context enabled!"
                        disabled={isLoading}
                        style={{
                            width: '100%',
                            minHeight: '40px',
                            maxHeight: '100px',
                            padding: '12px 50px 12px 12px',
                            border: '1px solid #8B4513',
                            borderRadius: '8px',
                            backgroundColor: '#FFF5EE',
                            color: '#5C4033',
                            fontSize: '14px',
                            resize: 'none',
                            outline: 'none',
                            fontFamily: 'inherit',
                            boxSizing: 'border-box',
                            lineHeight: '1.4',
                            transition: 'border-color 0.2s ease'
                        }}
                        onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#5C4033';
                            e.currentTarget.style.boxShadow = '0 0 0 2px rgba(92, 64, 51, 0.1)';
                        }}
                        onBlur={(e) => {
                            e.currentTarget.style.borderColor = '#8B4513';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    />
                    <button
                        onClick={() => isStreaming ? stopResponse() : sendMessage()}
                        disabled={!inputValue.trim() && !isStreaming}
                        style={{
                            position: 'absolute',
                            right: '8px',
                            bottom: '8px',
                            width: '36px',
                            height: '36px',
                            backgroundColor: isStreaming ? '#dc2626' : ((!inputValue.trim() || isLoading) ? '#9B5523' : '#5C4033'),
                            color: '#FFF5EE',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: (!inputValue.trim() && !isStreaming) ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            marginBottom: '10px'
                        }}
                        onMouseEnter={(e) => {
                            if (!(!inputValue.trim() && !isStreaming)) {
                                if (isStreaming) {
                                    e.currentTarget.style.backgroundColor = '#b91c1c';
                                } else {
                                    e.currentTarget.style.backgroundColor = '#4a3628';
                                }
                                e.currentTarget.style.transform = 'scale(1.05)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!(!inputValue.trim() && !isStreaming)) {
                                if (isStreaming) {
                                    e.currentTarget.style.backgroundColor = '#dc2626';
                                } else {
                                    e.currentTarget.style.backgroundColor = '#5C4033';
                                }
                                e.currentTarget.style.transform = 'scale(1)';
                            }
                        }}
                    >
                        {isStreaming ? '⏹' : (isLoading ? (
                            <div style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid rgba(255,255,255,0.3)',
                                borderTopColor: '#FFF5EE',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}/>
                        ) : '➤')}
                    </button>
                </div>

                {/* Compact Status Bar */}
                <div style={{
                    fontSize: '11px', // Reduced from 12px
                    color: '#8B4513',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '6px' // Reduced from 8px
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px', // Reduced from 12px
                        flexWrap: 'wrap'
                    }}>
                        <span>Enter to send • Shift+Enter for new line</span>
                        {contextSummary.hasEnhancedContext && (
                            <>
                                <span>•</span>
                                <span style={{
                                    color: '#4caf50',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '3px'
                                }}>
                        ✓ Enhanced
                    </span>
                            </>
                        )}
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}>
    <span style={{
        color: selectedFiles.length > 0 ? '#4B5320' : '#8B4513',
        fontWeight: selectedFiles.length > 0 ? '600' : '400'
    }}>
        {/* 🔧 FIXED: Update text to reflect new logic */}
        {selectedFiles.length > 0
            ? `${selectedFiles.length} files`
            : 'No files'
        }
    </span>
                        <span>•</span>
                        <span>{messages.length} msgs</span>
                    </div>

                </div>
            </div>

            {/* CSS Animations */}
            <style>
                {`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                @keyframes blink {
                    0%, 50% { opacity: 1; }
                    51%, 100% { opacity: 0; }
                }
                `}
            </style>
        </div>
    );
};

export default AIStudioChat;